import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonService } from '@src/app/services/common.service';
import { RequestService } from '@src/app/services/request.service';
import { TelegramService } from '@src/app/services/telegram.service';
import { UserService } from '@src/app/services/user.service';
import { WalletStatusService } from '@src/app/services/wallet-status.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { ChainEnum, getChainImg } from '@src/app/shared/types/wallet-chain.type';
import { tokenImage } from '@src/app/shared/utils/file';
import { formatNumber } from '@src/app/shared/utils/number.util';
import * as dayjs from 'dayjs';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { interval, Subject, takeUntil } from 'rxjs';

export interface Feature {
    id: FeatureType;
    name: string;
    value: any;
    events: {
        data: any[];
    };
    timestamp: string;
}

export enum FeatureType {
    ENS = 'ens',
    OPENSEA = 'opensea',
    UNISWAP_V3_TRADING = 'uniswap_v3_trading',
    UNISWAP_V3_LIQUIDITY = 'uniswap_v3_liquidity',
    DEPOSIT_TO_L2 = 'deposit_to_l2',
    CONTRACT_DEPLOYER = 'contract_deployer',

    TRANSFER_MORE_THAN_100ETH = 'transfer_more_100_eth',
    MULTI_SIG_OWNER = 'multi_sig_owner',
    GITCOIN_DONATION = 'gitcoin_donation',
    COLLATERAL = 'collateral',
    ONCHAIN_MESSAGE = 'onchain_message',
}

export const FeatureNameMap: Record<FeatureType, string> = {
    [FeatureType.ENS]: 'ENS',
    [FeatureType.OPENSEA]: 'OpenSea volume',
    [FeatureType.UNISWAP_V3_TRADING]: 'Uniswap V3 Trading',
    [FeatureType.UNISWAP_V3_LIQUIDITY]: 'Liquidity provider',
    [FeatureType.DEPOSIT_TO_L2]: 'Bridge volume',
    [FeatureType.CONTRACT_DEPLOYER]: 'Contract deployer',

    [FeatureType.TRANSFER_MORE_THAN_100ETH]: 'Transfer >100 ETH',
    [FeatureType.MULTI_SIG_OWNER]: 'Multi-sig owner',
    [FeatureType.GITCOIN_DONATION]: 'Gitcoin donation',
    [FeatureType.COLLATERAL]: 'Collateral',
    [FeatureType.ONCHAIN_MESSAGE]: 'Onchain message',
};
@Component({
    selector: 'app-address-features',
    standalone: true,
    imports: [CommonModule, SharedModule, NzSkeletonModule, NzDividerModule, NzImageModule],
    templateUrl: './address-features.component.html',
    styleUrls: ['./address-features.component.less'],
})
export class AddressFeaturesComponent implements OnInit, OnDestroy {
    @Input() walletAddress: string;
    FeatureType = FeatureType;
    ChainEnum = ChainEnum;
    FeatureNameMap = FeatureNameMap;
    tokenImage = tokenImage;
    formatNumber = formatNumber;
    getChainImg = getChainImg;

    features: Feature[];
    leftSeconds = 0;
    fetching = false;
    refetchDuration = 12;

    expandedFeature = new Set<Feature>();

    destroy$ = new Subject();

    get isMe() {
        return this.walletAddress?.toLowerCase() === this.walletStatusService.walletAddress?.toLowerCase();
    }

    get connectWalletVisible() {
        if (this.isMe) {
            // TODO: 确认是否可以优化
            return this.userService.isOnlyTg || (this.tgService.isTGLogin && !this.walletStatusService.isWalletConnected);
        }

        return false;
    }

    constructor(
        private requestService: RequestService,
        private tgService: TelegramService,
        public userService: UserService,
        public commonService: CommonService,
        public walletStatusService: WalletStatusService
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    ngOnInit(): void {
        if (this.walletAddress) {
            this.startFetching();
            interval(1000)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.startFetching());
        }
    }

    // Fetch features every 12 seconds
    startFetching(): void {
        if (this.fetching) {
            return;
        }

        if (this.leftSeconds <= 1) {
            this.fetching = true;
            this.fetchFeatures()
                .then(({ features }) => {
                    // Store the IDs of currently expanded features
                    const expandedFeatureIds = Array.from(this.expandedFeature).map(f => f.id);

                    this.features = features.filter(f => f.events?.data?.length > 0);

                    // Restore the expanded state for features that still exist
                    this.expandedFeature = new Set(this.features.filter(f => expandedFeatureIds.includes(f.id)));
                })
                .catch(error => {
                    console.error('Error fetching features:', error);
                })
                .finally(() => {
                    this.fetching = false;
                    this.leftSeconds = this.refetchDuration;
                });
        } else {
            this.leftSeconds--;
        }
    }

    fetchFeatures(): Promise<{ features: Feature[] }> {
        return this.requestService.sendRequest<{ features: Feature[] }>({
            method: 'GET',
            api: 'socialscan',
            url: `/v2/aci/${this.walletAddress}/all_features`,
        });
    }

    getTime(dateTime: string | any): string {
        const day = dayjs(dateTime);
        const now = dayjs();
        const diff = now.diff(day, 'day');
        return diff === 0 ? day.format('HH:mm:ss') : day.format('YYYY/MM/DD');
    }

    toggleExpand(feature: Feature) {
        if (this.expandedFeature.has(feature)) {
            this.expandedFeature.delete(feature);
        } else {
            this.expandedFeature.add(feature);
        }

        this.expandedFeature = new Set(this.expandedFeature);
    }
}
